import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { AppCard } from '@components/app-card';
import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconOpen } from '@components/icons/open';
import { IconQuestion } from '@components/icons/question';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { IconRatingPoor } from '@components/icons/rating-poor';
import { IconRatingVeryGood } from '@components/icons/rating-very-good';
import { IconRatingVeryPoor } from '@components/icons/rating-very-poor';
import { IconSend } from '@components/icons/send';
import { IconTeam } from '@components/icons/team';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTextShort } from '@components/icons/text-short';
import { IconTrash } from '@components/icons/trash';
import { RatingBox } from '@components/rating-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { ToggleGroup } from '@components/toggle-group';
import { abbreviateDate } from '@utils/date';
import { match } from '@utils/match';

import { api } from 'api';
import { IntegrationIcon } from 'components/IntegrationIcon';
import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

import {
  ApplicationParams,
  HireflixErrorTypes,
  HireflixInterview,
  HireflixPosition,
  HireflixQuestion,
  HireflixUsersRating,
  Rating,
} from './types';
import { getErrorMessage } from './utils';

export const HireflixApplicationApp = observer(() => {
  const { applicationStore } = useStore();
  const { jobID, applicationID } = useParams() as ApplicationParams;
  const application = applicationStore.items.get(applicationID);

  const linkedPositionQuery = useQuery({
    queryKey: ['hireflixLinkedPosition'],
    queryFn: () => api.integrations.hireflixGetLinkedJobPosition(jobID),
  });

  const interviewQuery = useQuery({
    queryKey: ['hireflixInterview', applicationID],
    queryFn: () => api.integrations.hireflixGetInterview(applicationID),
    enabled: linkedPositionQuery.isSuccess && Boolean(linkedPositionQuery.data),
  });

  if (linkedPositionQuery.isSuccess) {
    if (!linkedPositionQuery.data) {
      return <HireflixJobNotLinked />;
    }
  } else {
    return <Loading />;
  }

  if (interviewQuery.isSuccess) {
    if (!interviewQuery.data) {
      application.load();
      return <HireflixInterviewNotSent positionData={linkedPositionQuery.data} />;
    }
  } else {
    return <Loading />;
  }
  if ('error' in interviewQuery.data) {
    return <Error error={interviewQuery.data.error} />;
  }

  return <HireflixInterviewSent appID={applicationID} data={interviewQuery.data} />;
});

interface ErrorProps {
  error: HireflixErrorTypes;
}

const Error = observer((props: ErrorProps) => {
  const { t } = useStore();
  const { error } = props;
  return (
    <Stack space={6}>
      <Header>
        <Stack space={2} orientation="horizontal" align="center">
          <IntegrationIcon integrationName="Hireflix" />
          <Text font="semibold14" text="Hireflix" />
        </Stack>
      </Header>
      <Card space={6}>{getErrorMessage(error, t)}</Card>
    </Stack>
  );
});

interface InterviewProps {
  appID: string;
  data: HireflixInterview;
}

const HireflixInterviewSent = observer((props: InterviewProps) => {
  const { applicationStore, t } = useStore();
  const { showError } = useToaster();
  const { data, appID } = props;
  const client = useQueryClient();
  const application = applicationStore.items.get(appID);
  const { hasRolePermission } = usePermissions();

  async function makeFinalist() {
    const data = client.getQueryData<HireflixInterview>(['hireflixInterview', appID]);
    client.setQueryData(['hireflixInterview', appID], { ...data, status: 'finalist' });
    const res = await api.integrations.hireflixMakeFinalist(appID);
    if (!res.error) {
      if (res.data && res.data.error) {
        showError(getErrorMessage(res.data.error, t));
        client.invalidateQueries(['hireflixInterview', appID]);
      } else {
        application.load();
      }
    } else {
      showError(t('integrations.hireflix.application.preselect.error'));
      client.invalidateQueries(['hireflixInterview', appID]);
    }
  }

  async function discard() {
    const data = client.getQueryData<HireflixInterview>(['hireflixInterview', appID]);
    client.setQueryData(['hireflixInterview', appID], { ...data, status: 'archived' });
    const res = await api.integrations.hireflixDiscardInterview(appID);
    if (!res.error) {
      if (res.data && res.data.error) {
        showError(getErrorMessage(res.data.error, t));
        client.invalidateQueries(['hireflixInterview', appID]);
      } else {
        application.load();
      }
    } else {
      showError(t('integrations.hireflix.application.discard.error'));
      client.invalidateQueries(['hireflixInterview', appID]);
    }
  }

  async function reset() {
    const data = client.getQueryData<HireflixInterview>(['hireflixInterview', appID]);
    client.setQueryData(['hireflixInterview', appID], { ...data, status: 'completed' });
    const res = await api.integrations.hireflixResetInterview(appID);
    if (!res.error) {
      if (res.data && res.data.error) {
        showError(getErrorMessage(res.data.error, t));
        client.invalidateQueries(['hireflixInterview', appID]);
      } else {
        application.load();
      }
    } else {
      showError(t('integrations.hireflix.application.reset.error'));
      client.invalidateQueries(['hireflixInterview', appID]);
    }
  }

  let content = null;

  if (!data) {
    content = <Loading />;
  } else if (data && !data.answered) {
    content = (
      <Card space={6}>
        <HireflixInterviewSentHeader data={data} appID={appID} />
      </Card>
    );
  } else {
    content = (
      <Card space={6}>
        <Stack space={4}>
          <Stack space={4}>
            <HireflixInterviewSentHeader data={data} appID={appID} />
            <Separator />
            {hasRolePermission('application:use-apps') && (
              <ToggleGroup.Root
                value={data.status}
                onChange={(value) => {
                  match(value, {
                    finalist: makeFinalist,
                    archived: discard,
                    completed: reset,
                  });
                }}
              >
                <ToggleGroup.Button
                  value="completed"
                  variant="orange"
                  text={t('integrations.hireflix.application.status.toevaluate')}
                />
                <ToggleGroup.Button
                  value="finalist"
                  variant="green"
                  text={t('integrations.hireflix.application.status.preselected')}
                />
                <ToggleGroup.Button
                  value="archived"
                  variant="red"
                  text={t('integrations.hireflix.application.status.discarded')}
                />
              </ToggleGroup.Root>
            )}
          </Stack>
          <Card spaceX={6} spaceY={4}>
            <Stack space={0} orientation="horizontal" justify="space-between" align="center">
              <Text text={t('integrations.hireflix.application.rating.overall')} font="bold18" />
              <RatingBox number={data.rating} />
            </Stack>
          </Card>
          <HireflixRatingsReceivedBox
            questions={data.questions}
            ratings={data.usersRatings}
            usersRated={data.usersRated}
          />
          {data.questions.map((question) => (
            <HireflixQuestionContainer
              key={question.id}
              question={question}
              ratings={data.ratings}
              appID={appID}
            />
          ))}
        </Stack>
      </Card>
    );
  }
  return (
    <Stack space={6}>
      <Header>
        <Stack space={2} orientation="horizontal" align="center">
          <IntegrationIcon integrationName="Hireflix" />
          <Text font="semibold14" text="Hireflix" />
        </Stack>
      </Header>
      {content}
    </Stack>
  );
});

interface HireflixQuestionContainerProps {
  question: HireflixQuestion;
  ratings: Rating;
  appID: string;
}

const HireflixQuestionContainer = observer((props: HireflixQuestionContainerProps) => {
  const { question, ratings, appID } = props;
  const { t } = useStore();
  const { showError } = useToaster();
  const { hasRolePermission } = usePermissions();

  const client = useQueryClient();

  async function questionRating(questionId: string, rating: number | null) {
    const data = client.getQueryData<HireflixInterview>(['hireflixInterview', appID]);
    if (!data) {
      return;
    }
    const ratings = { ...data.ratings, [questionId]: rating };
    client.setQueryData(['hireflixInterview', appID], { ...data, ratings });
    const success = await api.integrations.hireflixQuestionRating(appID, questionId, rating);
    if (!success) {
      showError(t('integrations.hireflix.application.question.rating.error'));
    }
    client.invalidateQueries(['hireflixInterview', appID]);
  }

  return (
    <QuestionContainer key={question.id}>
      <Stack space={6}>
        <Stack space={1} orientation="horizontal" align="center">
          <IconTextShort />
          <Text font="bold14" text={question.title} />
        </Stack>
        <Text font="regular16" text={question.description} />
        {question.answer?.url ? (
          <video controls controlsList="nodownload" width="100%">
            <source src={question.answer.url} />
          </video>
        ) : (
          <Text font="regular16" text={t('integrations.hireflix.application.no.answer.found')} />
        )}

        {hasRolePermission('application:use-apps') && (
          <Card spaceX={6} spaceY={4}>
            <Stack space={0} orientation="horizontal" justify="space-between">
              <IconButton
                variant={ratings[question.id] === 0 ? 'ghost-darkred' : 'ghost-purple'}
                icon={<IconRatingVeryPoor />}
                onClick={() => questionRating(question.id, ratings[question.id] === 0 ? null : 0)}
                tooltip={t('integrations.hireflix.application.rating.strongno')}
              />
              <IconButton
                variant={ratings[question.id] === 2.5 ? 'ghost-red' : 'ghost-purple'}
                icon={<IconRatingPoor />}
                onClick={() =>
                  questionRating(question.id, ratings[question.id] === 2.5 ? null : 2.5)
                }
                tooltip={t('integrations.hireflix.application.rating.no')}
              />
              <IconButton
                variant={ratings[question.id] === 5 ? 'ghost-orange' : 'ghost-purple'}
                icon={<IconRatingMedium />}
                onClick={() => questionRating(question.id, ratings[question.id] === 5 ? null : 5)}
                tooltip={t('integrations.hireflix.application.rating.average')}
              />
              <IconButton
                variant={ratings[question.id] === 7.5 ? 'ghost-green' : 'ghost-purple'}
                icon={<IconRatingGood />}
                onClick={() =>
                  questionRating(question.id, ratings[question.id] === 7.5 ? null : 7.5)
                }
                tooltip={t('integrations.hireflix.application.rating.yes')}
              />
              <IconButton
                variant={ratings[question.id] === 10 ? 'ghost-darkgreen' : 'ghost-purple'}
                icon={<IconRatingVeryGood />}
                onClick={() => questionRating(question.id, ratings[question.id] === 10 ? null : 10)}
                tooltip={t('integrations.hireflix.application.rating.strongyes')}
              />
            </Stack>
          </Card>
        )}
        {hasRolePermission('application:use-apps') && <Separator />}
      </Stack>
    </QuestionContainer>
  );
});

const HireflixInterviewSentHeader = observer((props: InterviewProps) => {
  const { appID, data } = props;
  const { applicationStore, lang, t, userStore } = useStore();
  const { showSuccess } = useToaster();
  const { showModal } = useModal();
  const client = useQueryClient();

  const [deleting, setDeleting] = useState(false);
  const application = applicationStore.items.get(appID);

  async function deleteInterviewConfirm(appID: string) {
    setDeleting(true);
    const success = await api.integrations.hireflixDeleteInterview(appID);
    if (success) {
      showSuccess(t('integrations.hireflix.application.delete.success'));
      client.resetQueries(['hireflixInterview', appID]);
      application.load();
    } else {
      showSuccess(t('integrations.hireflix.application.delete.error'));
    }
    setDeleting(false);
    return true;
  }

  function deleteInterview() {
    showModal('COMMON_REMOVE_CONFIRM', {
      data: { id: appID, confirmText: t('integrations.hireflix.application.delete.confirm') },
      confirm: deleteInterviewConfirm,
    });
  }

  return (
    <Stack space={4}>
      <Stack space={0} orientation="horizontal" justify="space-between" align="center">
        <Text font="bold16" text={data.position.name} />
        <IconButton
          onClick={deleteInterview}
          disabled={deleting}
          icon={<IconTrash />}
          variant="ghost-purple"
        />
      </Stack>
      <Stack space={1}>
        <Text font="semibold14" text={t('integrations.hireflix.application.sentby.label')} />
        <Text font="regular16" text={userStore.items.get(data.sentBy)?.name} />
      </Stack>
      <Stack space={1}>
        <Text font="semibold14" text={t('integrations.hireflix.application.status.label')} />
        <Text
          font="regular16"
          text={`${t('integrations.hireflix.application.status.invited')} • ${abbreviateDate(
            data.dateSent,
            lang,
            'datetime',
          )}`}
        />
        {data.dateCompleted && (
          <Text
            font="regular16"
            text={`${t('integrations.hireflix.application.status.completed')} • ${abbreviateDate(
              data.dateCompleted,
              lang,
              'datetime',
            )}`}
          />
        )}
      </Stack>
    </Stack>
  );
});

const HireflixJobNotLinked = observer(() => {
  const { jobID } = useParams() as ApplicationParams;
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  return (
    <Stack space={6}>
      <Header>
        <Stack space={2} orientation="horizontal" align="center">
          <IntegrationIcon integrationName="Hireflix" />
          <Text font="semibold14" text="Hireflix" />
        </Stack>
        {hasRolePermission('application:use-apps') && (
          <Button
            as="a"
            href={
              `/jobs/${jobID}/integrations/` /* find a better way to go straight to the integration*/
            }
            variant="secondary"
            text={t('integrations.hireflix.application.linkposition')}
            iconRight={<IconTextFormatLink />}
          />
        )}
      </Header>
      <AppCard variant="gray" padding="lg">
        <Stack space={2} orientation="horizontal" align="center">
          <IconQuestion color="purple80" />
          <Text text={t('integrations.hireflix.application.jobnotlinked')} />
        </Stack>
      </AppCard>
    </Stack>
  );
});

interface NotSentProps {
  positionData: HireflixPosition;
}

const HireflixInterviewNotSent = observer((props: NotSentProps) => {
  const { t } = useStore();
  const { showModal } = useModal();
  const { positionData } = props;
  const { applicationID } = useParams() as ApplicationParams;
  const { hasRolePermission } = usePermissions();
  return (
    <Stack space={6}>
      <Header>
        <Stack space={2} orientation="horizontal" align="center">
          <IntegrationIcon integrationName="Hireflix" size={24} />
          <Text font="semibold14" text="Hireflix" />
        </Stack>
        {hasRolePermission('application:use-apps') && (
          <Button
            variant="secondary"
            text={t('integrations.hireflix.application.sendinterview')}
            iconRight={<IconSend />}
            onClick={() => {
              if ('error' in positionData) return;
              showModal('INTEGRATION_HIREFLIX_SEND_INTERVIEW', {
                appID: applicationID,
                positionData,
              });
            }}
          />
        )}
      </Header>
      <AppCard variant="gray" padding="lg">
        <Stack space={2} orientation="horizontal" align="center">
          <IconQuestion color="purple80" />
          <Text text={t('integrations.hireflix.application.interviewnotsent')} />
        </Stack>
      </AppCard>
    </Stack>
  );
});

interface RatingsReceivedProps {
  questions: HireflixQuestion[];
  ratings: HireflixUsersRating;
  usersRated: number[];
}

const HireflixRatingsReceivedBox = observer((props: RatingsReceivedProps) => {
  const { t, userStore } = useStore();
  const { showModal } = useModal();
  const { questions, ratings, usersRated } = props;

  return (
    <Card space={4} variant="transparent">
      <Stack space={0} orientation="horizontal" justify="space-between">
        <Stack space={4} orientation="horizontal" align="center">
          <IconTeam />
          <Text
            font="semibold14"
            text={t('integrations.hireflix.application.ratings.received', usersRated.length)}
          />
        </Stack>
        <Stack space={6} orientation="horizontal" align="center">
          <Stack space={2} orientation="horizontal">
            {usersRated.map((userId) => {
              const user = userStore.items.get(userId);
              return <Avatar key={userId} size="md" name={user.name} src={user.picture} />;
            })}
          </Stack>
          <IconButton
            icon={<IconOpen />}
            variant="ghost-purple"
            onClick={() => showModal('INTEGRATION_HIREFLIX_REVIEWS', { questions, ratings })}
            tooltip={t('integrations.hireflix.application.ratings.viewall')}
          />
        </Stack>
      </Stack>
    </Card>
  );
});

const Header = styled(Stack).attrs({
  space: 0,
  orientation: 'horizontal',
  justify: 'space-between',
})({});

const QuestionContainer = styled.div(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  div: {
    gridColumn: '2 / span 4',
  },
  marginTop: '2rem',
  video: {
    borderRadius: '0.25rem',
  },
}));
